/**
 * Gelaendemodell (Kapitel N, die zweite Haelfte).
 *
 * Die Spec beschreibt als Fernziel ein Gelaendemodell mit Textur und darueber
 * einen LiDAR-Scan, um die Huellenlinien von Gebaeuden und Baeumen zu bekommen.
 * Genau das ist hier gebaut — nur ohne den Kauf von Daten:
 *
 * Das BEV stellt aus der Laserscan-Befliegung zwei Raster mit 1 m Weite bereit,
 * beide kostenfrei ueber data.bev.gv.at:
 *   DGM — das Gelaende ohne alles, was darauf steht
 *   DOM — die Oberflaeche mit Baeumen und Gebaeuden
 *
 * Die Differenz der beiden ist die Huellenlinie, um die es geht: Wo das DOM
 * zwei Meter ueber dem DGM liegt, steht etwas. Daraus werden hier
 * Verschattungsobjekte — dieselben, die man sonst von Hand setzt (PV-N01).
 *
 * Zwei Dinge zur Einordnung:
 *   Die Hoehengenauigkeit liegt bei rund einem halben Meter. Fuer einen Baum
 *   neben dem Haus ist das reichlich genau; fuer die Frage, ob ein Modul um
 *   09:15 im Schatten liegt, bleibt es eine Schaetzung.
 *   Die Daten sind eine Momentaufnahme der Befliegung. Ein Baum waechst, ein
 *   Nachbargebaeude kommt dazu. Deshalb traegt jedes gefundene Objekt den
 *   Stichtag mit, und der Nutzer kann es ueberschreiben.
 *
 * Dieses Modul kennt keine Dateiformate und keine Kacheln. Es bekommt ein
 * Raster und rechnet.
 */
import { konvexeHuelle } from './verschattung.js';
import { flaecheninhalt, huellkasten } from './polygon.js';
import { bogenInGrad, gradInBogen, normiereGrad } from './typen.js';
export const rasterwert = (raster, spalte, zeile) => raster.werte[zeile * raster.spalten + spalte] ?? Number.NaN;
export const rasterpunkt = (raster, spalte, zeile) => ({
    x: raster.ursprung.x + spalte * raster.zellgroesseM,
    y: raster.ursprung.y - zeile * raster.zellgroesseM,
});
/**
 * DOM minus DGM — was auf dem Gelaende steht.
 *
 * Beide Raster muessen dieselbe Geometrie haben. Das ist keine Schikane: Wer
 * hier zwei verschieden aufgeloeste Raster voneinander abzieht, bekommt
 * Objekthoehen, die niemandem auffallen und alles Weitere verfaelschen.
 */
export function objekthoehen(dom, dgm) {
    if (dom.spalten !== dgm.spalten ||
        dom.zeilen !== dgm.zeilen ||
        Math.abs(dom.zellgroesseM - dgm.zellgroesseM) > 1e-6 ||
        Math.abs(dom.ursprung.x - dgm.ursprung.x) > 1e-6 ||
        Math.abs(dom.ursprung.y - dgm.ursprung.y) > 1e-6) {
        throw new Error('objekthoehen: DOM und DGM haben nicht dieselbe Rastergeometrie');
    }
    const werte = new Float32Array(dom.werte.length);
    for (let i = 0; i < werte.length; i++) {
        const oben = dom.werte[i];
        const unten = dgm.werte[i];
        // Ein negativer Wert ist Messrauschen, kein Loch im Boden.
        werte[i] = Number.isFinite(oben) && Number.isFinite(unten) ? Math.max(0, oben - unten) : Number.NaN;
    }
    return { ...dom, werte };
}
/**
 * Zusammenhaengende Bereiche ueber der Mindesthoehe zu Objekten machen.
 *
 * Das Verfahren ist bewusst schlicht: schwellen, zusammenhaengende Zellen
 * einsammeln, konvexe Huelle um den Fuss legen, hoechsten Wert als Hoehe
 * nehmen. Eine konvexe Huelle ist fuer einen Baum genau richtig und fuer ein
 * L-foermiges Gebaeude zu grosszuegig — der Schatten faellt dann etwas zu
 * gross aus. Das ist die sichere Richtung.
 */
export function findeVerschattungsobjekte(ndom, optionen = {}) {
    const mindesthoehe = optionen.mindesthoeheM ?? 2;
    const mindestflaeche = optionen.mindestflaecheM2 ?? 4;
    const zellflaeche = ndom.zellgroesseM ** 2;
    const mindestzellen = Math.max(1, Math.ceil(mindestflaeche / zellflaeche));
    const besucht = new Uint8Array(ndom.werte.length);
    const objekte = [];
    for (let zeile = 0; zeile < ndom.zeilen; zeile++) {
        for (let spalte = 0; spalte < ndom.spalten; spalte++) {
            const start = zeile * ndom.spalten + spalte;
            if (besucht[start])
                continue;
            const wert = ndom.werte[start];
            if (!Number.isFinite(wert) || wert < mindesthoehe) {
                besucht[start] = 1;
                continue;
            }
            // Flutfuellung mit einem Stapel — rekursiv waere bei einem Waldstueck
            // schnell der Aufrufstapel voll.
            const zellen = [];
            const stapel = [start];
            besucht[start] = 1;
            while (stapel.length > 0) {
                const aktuell = stapel.pop();
                zellen.push(aktuell);
                const z = Math.floor(aktuell / ndom.spalten);
                const s = aktuell % ndom.spalten;
                for (let dz = -1; dz <= 1; dz++) {
                    for (let ds = -1; ds <= 1; ds++) {
                        if (dz === 0 && ds === 0)
                            continue;
                        const nz = z + dz;
                        const ns = s + ds;
                        if (nz < 0 || nz >= ndom.zeilen || ns < 0 || ns >= ndom.spalten)
                            continue;
                        const nachbar = nz * ndom.spalten + ns;
                        if (besucht[nachbar])
                            continue;
                        const nachbarwert = ndom.werte[nachbar];
                        if (!Number.isFinite(nachbarwert) || nachbarwert < mindesthoehe) {
                            besucht[nachbar] = 1;
                            continue;
                        }
                        besucht[nachbar] = 1;
                        stapel.push(nachbar);
                    }
                }
            }
            if (zellen.length < mindestzellen)
                continue;
            objekte.push(zuObjekt(ndom, zellen, objekte.length));
        }
    }
    objekte.sort((links, rechts) => rechts.hoeheM - links.hoeheM);
    return optionen.hoechstens ? objekte.slice(0, optionen.hoechstens) : objekte;
}
function zuObjekt(ndom, zellen, laufend) {
    const punkte = [];
    let hoechste = 0;
    let summe = 0;
    let summeQuadrate = 0;
    for (const zelle of zellen) {
        const zeile = Math.floor(zelle / ndom.spalten);
        const spalte = zelle % ndom.spalten;
        punkte.push(rasterpunkt(ndom, spalte, zeile));
        const wert = ndom.werte[zelle];
        if (wert > hoechste)
            hoechste = wert;
        summe += wert;
        summeQuadrate += wert * wert;
    }
    const huelle = konvexeHuelle(punkte);
    const anzahl = zellen.length;
    const mittel = summe / anzahl;
    const streuung = Math.sqrt(Math.max(0, summeQuadrate / anzahl - mittel * mittel));
    const flaecheM2 = anzahl * ndom.zellgroesseM ** 2;
    // Ein Gebaeude hat eine flache Oberkante und eine kantige Grundflaeche, ein
    // Baum eine runde Krone mit abfallendem Rand. Mehr als ein Anhaltspunkt ist
    // das nicht — deshalb steht die Sicherheit dabei und der Nutzer kann die Art
    // aendern.
    const kasten = huellkasten(huelle);
    const kastenflaeche = (kasten.maxX - kasten.minX) * (kasten.maxY - kasten.minY);
    const fuelligkeit = kastenflaeche > 0 ? flaecheninhalt(huelle) / kastenflaeche : 0;
    const flach = streuung < 1.2;
    const kantig = fuelligkeit > 0.72;
    const art = flach && kantig ? 'gebaeude' : 'baum';
    const sicherheit = Math.min(1, Math.max(0, art === 'gebaeude'
        ? (fuelligkeit - 0.6) * 2 + (1.2 - streuung) * 0.3
        : (1 - fuelligkeit) * 1.2 + Math.min(streuung, 3) * 0.2));
    return {
        id: `gelaende-${laufend + 1}`,
        name: `${art === 'gebaeude' ? 'Gebäude' : 'Baum'} ${laufend + 1}`,
        art,
        grundflaeche: huelle,
        hoeheM: Number(hoechste.toFixed(2)),
        flaecheM2: Number(flaecheM2.toFixed(1)),
        sicherheit: Number(sicherheit.toFixed(2)),
        ...(ndom.stichtag ? { stichtag: ndom.stichtag } : {}),
    };
}
/**
 * Horizontlinie aus dem Gelaendemodell.
 *
 * Von der Position aus wird in jede Richtung hinausgeschaut und der groesste
 * Hoehenwinkel gemerkt. Gerechnet wird auf dem DGM — Baeume und Gebaeude sind
 * die Objekte von oben, nicht der Horizont.
 */
export function horizontAusGelaende(dgm, standort, suche = {}) {
    const schrittGrad = suche.schrittGrad ?? 2;
    const reichweiteM = suche.reichweiteM ?? 3000;
    const augpunkt = hoeheAn(dgm, standort) + (suche.hoeheUeberGrundM ?? 0);
    const schritte = Math.round(360 / schrittGrad);
    const hoehenwinkel = new Array(schritte).fill(0);
    for (let i = 0; i < schritte; i++) {
        const azimut = gradInBogen(i * schrittGrad);
        const richtung = { x: Math.sin(azimut), y: Math.cos(azimut) };
        let groesster = 0;
        // In Schritten der Zellgroesse hinaus, aber nie feiner als noetig.
        for (let d = dgm.zellgroesseM; d <= reichweiteM; d += Math.max(dgm.zellgroesseM, d / 50)) {
            const punkt = { x: standort.x + richtung.x * d, y: standort.y + richtung.y * d };
            const hoehe = hoeheAn(dgm, punkt);
            if (!Number.isFinite(hoehe))
                break; // aus dem Raster hinaus
            const winkel = bogenInGrad(Math.atan2(hoehe - augpunkt, d));
            if (winkel > groesster)
                groesster = winkel;
        }
        hoehenwinkel[i] = Number(groesster.toFixed(2));
    }
    return { hoehenwinkel, schrittGrad };
}
/** Hoehe an einem beliebigen Punkt, bilinear zwischen den Zellen. */
export function hoeheAn(raster, punkt) {
    const spalte = (punkt.x - raster.ursprung.x) / raster.zellgroesseM;
    const zeile = (raster.ursprung.y - punkt.y) / raster.zellgroesseM;
    if (spalte < 0 || zeile < 0 || spalte > raster.spalten - 1 || zeile > raster.zeilen - 1) {
        return Number.NaN;
    }
    const s0 = Math.floor(spalte);
    const z0 = Math.floor(zeile);
    const s1 = Math.min(s0 + 1, raster.spalten - 1);
    const z1 = Math.min(z0 + 1, raster.zeilen - 1);
    const fs = spalte - s0;
    const fz = zeile - z0;
    const a = rasterwert(raster, s0, z0);
    const b = rasterwert(raster, s1, z0);
    const c = rasterwert(raster, s0, z1);
    const d = rasterwert(raster, s1, z1);
    if (![a, b, c, d].every(Number.isFinite))
        return Number.NaN;
    return a * (1 - fs) * (1 - fz) + b * fs * (1 - fz) + c * (1 - fs) * fz + d * fs * fz;
}
/** Steht die Sonne ueber dem Horizont? */
export function sonneUeberHorizont(horizont, sonnenazimutGrad, sonnenhoeheGrad) {
    if (sonnenhoeheGrad <= 0)
        return false;
    const index = Math.round(normiereGrad(sonnenazimutGrad) / horizont.schrittGrad) %
        horizont.hoehenwinkel.length;
    return sonnenhoeheGrad > (horizont.hoehenwinkel[index] ?? 0);
}
/**
 * Wie viel der Horizont ueberhaupt ausmacht — der Anteil der Himmelsrichtungen,
 * in denen er nennenswert ansteigt. Taugt als Kennzahl fuer die Anzeige.
 */
export function horizontkennzahl(horizont) {
    let summe = 0;
    let hoechster = 0;
    let azimut = 0;
    horizont.hoehenwinkel.forEach((winkel, i) => {
        summe += winkel;
        if (winkel > hoechster) {
            hoechster = winkel;
            azimut = i * horizont.schrittGrad;
        }
    });
    return {
        mittelGrad: Number((summe / horizont.hoehenwinkel.length).toFixed(2)),
        hoechsterGrad: hoechster,
        azimutDesHoechsten: azimut,
    };
}
//# sourceMappingURL=gelaende.js.map